/*
  pp-money.js — locale-aware currency formatter

  Why this exists: the shop's moneyFormat is hardcoded `€{{amount_with_comma_separator}}`
  (symbol-before-amount). When Shopify Markets switches currency (e.g. to SEK),
  it swaps the symbol but keeps the position — yielding "kr375" instead of
  the Swedish-correct "375 kr".

  Strategy:
    1. Provide window.ppFormatMoney(cents) — uses Intl.NumberFormat with the
       active currency code + document lang to get locale-correct placement.
    2. On load, reformat every [data-cents] element.
    3. MutationObserver re-applies the format whenever something downstream
       (frame-change handler, size-modal sync) overwrites a [data-cents]
       element with a server-rendered string.

  Element contract:
    <span class="..." data-cents="{{ variant.price }}">{{ variant.price | money_without_trailing_zeros }}</span>
    JS that updates this element should do:
      el.dataset.cents = newCents;
      el.textContent = window.ppFormatMoney(newCents);
*/
(function () {
  if (window.ppFormatMoney) return;

  function getCurrencyCode() {
    return (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR';
  }

  // Map currency → the locale whose formatting convention matches that
  // currency's home country. This makes USD render as "$41.00" even when
  // the page's <html lang> is Swedish, and SEK as "375 kr" even on the
  // English store. The page-language formatting (e.g. en-SE applying decimal
  // commas to dollars) is the wrong default for e-commerce display.
  var CURRENCY_LOCALES = {
    USD: 'en-US',
    GBP: 'en-GB',
    CAD: 'en-CA',
    AUD: 'en-AU',
    NZD: 'en-NZ',
    EUR: 'de-DE',
    SEK: 'sv-SE',
    NOK: 'nb-NO',
    DKK: 'da-DK',
    ISK: 'is-IS',
    JPY: 'ja-JP',
    CHF: 'de-CH',
    CZK: 'cs-CZ',
    PLN: 'pl-PL',
    HUF: 'hu-HU',
    KRW: 'ko-KR',
    SGD: 'en-SG',
    HKD: 'en-HK',
    INR: 'en-IN',
    MXN: 'es-MX',
    BRL: 'pt-BR',
    ZAR: 'en-ZA',
    AED: 'en-AE',
    TRY: 'tr-TR',
    THB: 'th-TH',
    MYR: 'ms-MY',
    IDR: 'id-ID',
    PHP: 'en-PH',
    VND: 'vi-VN',
    CLP: 'es-CL',
    CNY: 'zh-CN'
  };

  function getLocale(currency) {
    if (currency && CURRENCY_LOCALES[currency]) return CURRENCY_LOCALES[currency];
    // Fallback to the document language if we don't have an explicit mapping.
    var l = (document.documentElement.lang || 'en').replace('_', '-');
    return l;
  }

  // Currencies whose smallest unit is the unit itself (no minor units).
  // For these, Shopify stores price already in whole units, not cents.
  var ZERO_DECIMAL = { JPY: 1, KRW: 1, ISK: 1, HUF: 1, CLP: 1, VND: 1 };

  function toAmount(cents, currency) {
    var n = Number(cents);
    if (!Number.isFinite(n)) return null;
    // Shopify always stores in the smallest unit. For JPY etc. that's the
    // whole yen (no division needed). For SEK/EUR/USD it's cents.
    return ZERO_DECIMAL[currency] ? n : n / 100;
  }

  function ppFormatMoney(cents, opts) {
    var currency = (opts && opts.currency) || getCurrencyCode();
    var locale = (opts && opts.locale) || getLocale(currency);
    var amount = toAmount(cents, currency);
    if (amount == null) return '';

    var noDecimals = !!(opts && opts.noDecimals);
    // If the amount is a whole number and shop convention is no-trailing-zeros,
    // strip them. Matches the `money_without_trailing_zeros` filter behavior.
    var isWhole = Math.abs(amount - Math.round(amount)) < 0.005;

    try {
      return amount.toLocaleString(locale, {
        style: 'currency',
        currency: currency,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: (noDecimals || isWhole) ? 0 : 2,
        maximumFractionDigits: (noDecimals || isWhole) ? 0 : 2
      });
    } catch (e) {
      // Browser doesn't recognize currency or locale — fallback to ISO code.
      try {
        return amount.toLocaleString(locale, {
          style: 'currency',
          currency: currency,
          minimumFractionDigits: (noDecimals || isWhole) ? 0 : 2,
          maximumFractionDigits: (noDecimals || isWhole) ? 0 : 2
        });
      } catch (_e) {
        return currency + ' ' + amount.toFixed(isWhole ? 0 : 2);
      }
    }
  }

  function reformatAll(root) {
    var scope = root || document;
    var els = scope.querySelectorAll('[data-cents]');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      var cents = el.getAttribute('data-cents');
      if (cents === '' || cents == null) {
        // Empty cents (e.g. no compare-at price) — blank the element.
        el.textContent = '';
        continue;
      }
      var n = parseInt(cents, 10);
      if (!Number.isFinite(n)) continue;
      var formatted = ppFormatMoney(n);
      if (el.textContent !== formatted) el.textContent = formatted;
    }
  }

  // Defense in depth: if something writes text into a [data-cents] element,
  // re-format from the source-of-truth cents value. Avoids feedback loops by
  // bailing when our own write produces the same text.
  var observer;
  function startObserver() {
    if (observer) return;
    observer = new MutationObserver(function (mutations) {
      var dirty = false;
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        var t = m.target;
        // Walk up to the [data-cents] host if a child text node changed
        while (t && t.nodeType === 1 && !t.hasAttribute('data-cents')) t = t.parentNode;
        if (t && t.nodeType === 1 && t.hasAttribute('data-cents')) {
          dirty = true;
          var cents = t.getAttribute('data-cents');
          if (cents === '' || cents == null) {
            if (t.textContent !== '') t.textContent = '';
            continue;
          }
          var n = parseInt(cents, 10);
          if (!Number.isFinite(n)) continue;
          var formatted = ppFormatMoney(n);
          if (t.textContent !== formatted) t.textContent = formatted;
        }
      }
      if (dirty && window.__ppMoneyDebug) console.log('[pp-money] reformat triggered');
    });
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      characterData: true
    });
  }

  window.ppFormatMoney = ppFormatMoney;
  window.ppReformatPrices = reformatAll;

  function init() {
    reformatAll();
    startObserver();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
